import { memo, useMemo } from 'react';
import equal from 'fast-deep-equal';
import { FormattedMessage } from 'react-intl';
import type { Category } from '@apiTypes';
import { Button, HStack, VStack, Text, Collapsible } from '@library';
import useCurrentCategoryFilters from '../../queries/useCurrentCategoryFilters';
import notEmpty from '../../../core/util/notEmpty';
import styles from './TireSearch.module.scss';
import TireFiltersBlock from './TireFiltersBlock';
import useTireSideFilterValues, {
    useTireFilterValues,
} from './queries/useTireSideFilterValues';
import useTireCommonFilterValues from './queries/useTireCommonFilterValues';
import { TireFilters } from './TireFilters';
import useTireParams from './hooks/useTireParams';
import { useTireSideFiltersEnabled } from './ProductListTireSearch';
import { tireCommonFilters, tirePairFilters, TireSide } from '.';

export const emptyFilters = {
    all: Object.fromEntries(
        [...tireCommonFilters, ...tirePairFilters].map(filter => {
            return [filter.field, []];
        })
    ),
    front: Object.fromEntries(
        tirePairFilters.map(filter => {
            return [filter.field, undefined];
        })
    ),
    back: Object.fromEntries(
        tirePairFilters.map(filter => {
            return [filter.field, undefined];
        })
    ),
};

const frontFilters = tirePairFilters.map(filter => {
    return {
        ...filter,
        placeholder: `TireSearch FilterPlaceholder_${filter.field}_front`,
    };
});

const backFilters = tirePairFilters.map(filter => {
    return {
        ...filter,
        placeholder: `TireSearch FilterPlaceholder_${filter.field}_back`,
    };
});

const allSideFilters = tirePairFilters.map(filter => {
    return {
        ...filter,
        placeholder: `TireSearch FilterPlaceholder_${filter.field}`,
    };
});

const commonFilters = tireCommonFilters.map(filter => {
    return {
        ...filter,
        placeholder: `TireSearch FilterPlaceholder_${filter.field}`,
    };
});

const TyreSearch = () => {
    const { enabled } = useTireSideFiltersEnabled();
    const frontValues = useTireSideFilterValues({
        side: TireSide.front,
        skip: !enabled,
    });
    const backValues = useTireSideFilterValues({
        side: TireSide.back,
        skip: !enabled,
    });
    const allValues = useTireFilterValues({ skip: enabled });
    const commonValues = useTireCommonFilterValues();

    const { category: filterData } = useCurrentCategoryFilters({
        includeValues: true,
    });
    const filters = (filterData as Category).filters.filter(notEmpty);

    const { params, updateParams } = useTireParams();

    const isNotEmpty = useMemo(() => {
        return !equal(emptyFilters, params);
    }, [params]);

    return (
        <div className={styles.container} data-test-class="tyreSearch">
            <Collapsible
                borderType="none"
                trigger={
                    <Text medium data-test-class={'collapseTypeSearch'}>
                        <FormattedMessage id="tyre search" />
                    </Text>
                }
                wide
                defaultExpand
                triggerSeparator
            >
                <VStack gap="small" data-test-class="tyreSearchFilters">
                    <div className={styles.grid}>
                        <div />
                        {tirePairFilters.map(filter => {
                            return (
                                <div key={filter.field}>
                                    <Text>
                                        <FormattedMessage
                                            id={`TireSearch FilterLabel_${filter.field}`}
                                        />
                                    </Text>
                                </div>
                            );
                        })}
                        {enabled && (
                            <>
                                <TireFiltersBlock
                                    typeCell={
                                        <FormattedMessage id="TireSearch FilterLabel Side_Front" />
                                    }
                                >
                                    <TireFilters
                                        type="single"
                                        filtersPossibleValues={frontValues}
                                        values={params.front}
                                        filters={frontFilters}
                                        onChange={value => {
                                            if (value.PIM_ATT_MATCHCODE) {
                                                updateParams({
                                                    front: {
                                                        ...emptyFilters.front,
                                                        ...value,
                                                    },
                                                });
                                            } else {
                                                updateParams({
                                                    front: {
                                                        ...params.front,
                                                        ...value,
                                                        PIM_ATT_MATCHCODE:
                                                            undefined,
                                                    },
                                                });
                                            }
                                        }}
                                        categoryFilters={filters}
                                    />
                                </TireFiltersBlock>
                                <TireFiltersBlock
                                    typeCell={
                                        <FormattedMessage id="TireSearch FilterLabel Side_Back" />
                                    }
                                >
                                    <TireFilters
                                        type="single"
                                        filtersPossibleValues={backValues}
                                        values={params.back}
                                        filters={backFilters}
                                        onChange={value => {
                                            if (value.PIM_ATT_MATCHCODE) {
                                                updateParams({
                                                    back: {
                                                        ...emptyFilters.back,
                                                        ...value,
                                                    },
                                                });
                                            } else {
                                                updateParams({
                                                    back: {
                                                        ...params.back,
                                                        ...value,
                                                        PIM_ATT_MATCHCODE:
                                                            undefined,
                                                    },
                                                });
                                            }
                                        }}
                                        categoryFilters={filters}
                                    />
                                </TireFiltersBlock>
                            </>
                        )}

                        {!enabled && (
                            <TireFiltersBlock
                                typeCell={
                                    <FormattedMessage id="TireSearch FilterLabel Specification All" />
                                }
                            >
                                <TireFilters
                                    type="multi"
                                    filtersPossibleValues={allValues}
                                    values={params.all}
                                    filters={allSideFilters}
                                    onChange={value => {
                                        updateParams({
                                            all: {
                                                ...params.all,
                                                ...value,
                                            },
                                        });
                                    }}
                                    categoryFilters={filters}
                                />
                            </TireFiltersBlock>
                        )}

                        <div className={styles.allHeaders} />
                        {tireCommonFilters.map(filter => {
                            return (
                                <div
                                    className={styles.allHeaders}
                                    key={filter.field}
                                >
                                    <Text>
                                        <FormattedMessage
                                            id={`TireSearch FilterLabel_${filter.field}`}
                                        />
                                    </Text>
                                </div>
                            );
                        })}
                        <TireFiltersBlock
                            typeCell={
                                <FormattedMessage id="TireSearch FilterLabel Specification All" />
                            }
                        >
                            <TireFilters
                                type="multi"
                                filtersPossibleValues={commonValues}
                                values={params.all}
                                filters={commonFilters}
                                onChange={value => {
                                    updateParams({
                                        all: {
                                            ...params.all,
                                            ...value,
                                        },
                                    });
                                }}
                                categoryFilters={filters}
                            />
                        </TireFiltersBlock>
                    </div>
                    <HStack gap="small" justify="spaceBetween" align="center">
                        <div />
                        {isNotEmpty && (
                            <Button
                                text
                                danger
                                inline
                                onClick={() => {
                                    updateParams(emptyFilters);
                                }}
                                data-test-class="tireSearchClearAll"
                            >
                                <FormattedMessage id="TireSearch ClearAll" />
                            </Button>
                        )}
                    </HStack>
                </VStack>
            </Collapsible>
        </div>
    );
};

export default memo(TyreSearch);
